import { badgeClient } from "./badge/client.ts";
import { BadgeController } from "./badge/controller.ts";
import { pageLog } from "./feedback/page-log.ts";
import Phaser from "phaser";
import { InterfaceSound, TICK_MS, dripLevel, effectNumber, uiScaleOf, type Appearance, type OfferedInvitation } from "odyssey-shared";
import { GPU_RENDER_CONFIG } from "../gpu-preference.ts";
import { admitAccount, watchLogoutElsewhere } from "./admission.ts";
import { authBase, changeLook, hudName, loadGrant, logoutEverywhere, renew, storeGrant, type LookOutcome, type Session } from "./auth.ts";
import { changePartyInvites } from "./party-invites-request.ts";
import type { PartyInvitesSetting } from "./menu/settings-page.ts";
import { changeCharacter as runChangeCharacter, type SelectReturnPage } from "./change-character.ts";
import { ChatAlerts, alertingFeed } from "./chat/alert.ts";
import { mountChat } from "./chat/mount.tsx";
import { readChatSize } from "./chat/size.ts";
import { ChatSizeSetting } from "./chat/sizing.ts";
import { ChatTextSetting, readChatText } from "./chat/text-size.ts";
import { httpProbe } from "./chat/probe.ts";
import { ChatStore } from "./chat/store.ts";
import { PartyStore, invitationsHeld } from "./party/store.ts";
import { ownVitalsOf } from "./party/vitals.ts";
import { partyingFeed } from "./party/feed.ts";
import { bindPartyOnMap } from "./party/map-feed.ts";
import { PartyPositionsBook } from "./party/positions.ts";
import type { CoveringPiece, PieceAlpha } from "daedalus-renderer-phaser";
import { isDevelopmentPage, isLocalPage, gatewayUrl } from "./endpoint.ts";
import { drawnPlayerNames } from "./player-names.ts";
import { cardFactsOf } from "./chat/hover-card.ts";
import { keepFresh, lapsed, renewLapsed } from "./keep-fresh.ts";
import type { FreshToken } from "./session-types.ts";
import { mountLoadingShell } from "./loading.tsx";
import { leaveByChoice, type ChosenLeavePage } from "./leave-flow.ts";
import { showStay } from "./leave-stay.tsx";
import { forgetOwnLeave, noteOwnLeave } from "./own-leave.ts";
import { logoutQuestion } from "./menu/leave-words.ts";
import { leaveQueue as runLeaveQueue } from "./queue/leave-queue.ts";
import { gameStartSteps, type LoadingFailure } from "./loading-state.ts";
import { actOnNotice } from "./notice-action.ts";
import { DiagnosticsFeed, diagnosticLine, watchDiagnostics } from "./hud/diagnostics.ts";
import { MaintenanceWarnings } from "./hud/maintenance-warning.ts";
import { DiscoveryToasts } from "./hud/discovery.ts";
import { LevelUpToast } from "./hud/level-up.ts";
import { bindBagKeys } from "./bag/keys.ts";
import { bindCharacterKeys } from "./character/keys.ts";
import { CharacterStore } from "./character/store.ts";
import { companionViews } from "./character/companions.ts";
import { CoordinatesSetting } from "./minimap/coordinates-setting.ts";
import { placeSpotsOf } from "./minimap/places-feed.ts";
import { CompassPlacesSetting } from "./minimap/places-setting.ts";
import { MinimapStore, forgetMinimapExpanded } from "./minimap/store.ts";
import { bindWorldMapKeys } from "./world-map/keys.ts";
import { MapPopout, browserPopout } from "./world-map/popout-host.ts";
import { WorldMapStore } from "./world-map/store.ts";
import { bindPageKeys } from "./bar/page-keys.ts";
import { BagStore } from "./bag/store.ts";
import { WalletStore } from "./wallet/store.ts";
import { HarvestStore } from "./harvest/store.ts";
import { TravelStore } from "./travel/store.ts";
import { bindChestKeys } from "./chest/keys.ts";
import { startEmoteWheel } from "./emotes/page.ts";
import { ChestStore } from "./chest/store.ts";
import { LootToasts } from "./chest/toasts.ts";
import { WindowOrder } from "./windows/order.ts";
import { sayOnHud } from "./message-line.ts";
import { bindFlagPanelKeys } from "./hud/flag-panel-keys.ts";
import { mountHud } from "./hud/mount.tsx";
import { BarStore } from "./bar/store.ts";
import { StanceStore } from "./fight/stance.ts";
import { ToolBelt } from "./belt/store.ts";
import { CompanionStore } from "./companion/store.ts";
import { PAGE_RULES } from "./rules.ts";
import { MusicScene } from "./audio/music.ts";
import { pageSound } from "./audio/page-sound.ts";
import { BarTicker } from "./bar/ticker.ts";
import { TipController } from "./bar/tips.ts";
import { hudLayout } from "./hud/slots.ts";
import { HudStore } from "./hud/store.ts";
import { showLogin, type HeldToken } from "./login.tsx";
import { entryLooks } from "./look-step.tsx";
import { mountConfirms } from "./logout-everywhere.tsx";
import { logOutWithRetry, mountLogoutRetry } from "./logout-retry.tsx";
import { TEMPORARY_LOOK_SCREEN, appearanceOfLook, type LookScreenPage } from "./menu/look-screen.ts";
import { mountGameMenu, type GameMenuControl } from "./menu/mount.tsx";
import { startModeration, type ModerationPage } from "./moderation/mount.tsx";
import { BarKeysSetting } from "./bar-keys-setting.ts";
import { BarPagingSetting } from "./bar-paging-setting.ts";
import { EmotesOthersSetting } from "./emotes-others-setting.ts";
import { pageAutoLoot } from "./ground/auto-loot.ts";
import { HealthNumbersSetting } from "./health-numbers-setting.ts";
import { IdleMarksSetting } from "./idle-marks-setting.ts";
import { pageRun } from "./run/page.ts";
import { NameplateSettings } from "./nameplate-settings.ts";
import { PAGE_MOTION } from "./page-motion.ts";
import { mirrorReducedMotion } from "./reduced-motion.ts";
import { mountReading } from "./reading/mount.tsx";
import { mountDialogue } from "./dialogue/mount.tsx";
import { DialogueStore } from "./dialogue/store.ts";
import { PAGE_CUES } from "./cues.ts";
import { DeathScreen } from "./death/death-screen.ts";
import { deathKeys, mountDeath } from "./death/mount.tsx";
import { ReadingStore } from "./reading/store.ts";
import { applyOverlayScale } from "./overlay.ts";
import { gameReport } from "./report.ts";
import { readLightingQuery } from "./world-ui/lighting/control.ts";
import { GameScene } from "./scene.ts";
import { localStore, readBarKeys, readBarLocked, writeBarLocked, readBarPaging, readEmotesOthers, readHealthNumbers, readIdleMarks, readNameplateChoices, readZoomStep } from "./settings.ts";
import { readFrameLimit, readInterfaceStep, readRenderDivisor } from "./display-settings.ts";
import { FrameLimiter } from "./frame-limit.ts";
import { RenderResolution, applyCanvasSize, parseRenderOverride } from "./render-resolution.ts";
import { PerfMonitor } from "./perf/monitor.ts";
import { bindPerfKey, sceneCounts } from "./perf/page.ts";
import { readPerformance } from "./perf/setting.ts";
import { PROBE_TIMEOUT_MS, StatusPoll, probeStatus } from "./status-poll.ts";
import { runningWorldVersion, takeVersionHint } from "./world-version.ts";
import { measureWindow, watchDevice } from "./viewport.ts";
import { mountTouchControls } from "./touch/mount-touch.ts";
import { readSafeInsets } from "./touch/safe-area.ts";
import { browserLacks } from "./browser-floor.ts";
import { showWebglMissing, webglAvailable } from "./webgl.ts";
import { contextLossNotice, watchContextLoss } from "../webgl-loss.ts";
import { InputRouter } from "../ui/input/router.ts";
import { suppressGameContextMenu } from "../ui/input/context-menu.ts";
import { isTextField } from "../ui/input/text-field.ts";
import { releaseFieldOnWorldPress } from "../ui/input/world-press.ts";
import { UiScale } from "./world-ui/ui-scale.ts";
import { WorldZoom, parseZoomOverride } from "./world-zoom.ts";
import { worldPointer } from "./world-pointer.ts";
import { benchAllowed } from "./bench-gate.ts";
import { crowdLooksAllowed } from "./looks/crowd-gate.ts";
import { pageFigures } from "./looks/page-figures.ts";
import { PAGE_ICONS } from "./icons/page.ts";
import { emojiFeed } from "./world-ui/emoji/art.ts";
import { trialLookAllowed } from "./trial-gate.ts";
import { readPageQuery } from "./page-query.ts";
import { PAGE_EFFECTS } from "./page-effects.ts";
import { effectsDetail, parseForcedClouds, parseForcedWeatherByMap, parseForcedWetByMap, parseForcedWind } from "./weather/weather-feed.ts";
import { parseForcedDrenched } from "./weather/drip-view.ts";
import { parseFeetMode } from "./weather/puddle-feet.ts";
import type { OwnLookLoader } from "./world-boot.ts";
import { devServerFetch } from "../dev-server-fetch.ts";
import { captureGameScreenshot } from "../screenshot.ts";
import { bearerFetch } from "./feedback/bearer-fetch.ts";
import { feedbackRoute } from "./feedback/route.ts";
import { sendFeedback, type FeedbackDestination } from "./feedback/send.ts";
import "../ui/generated/fonts.css";
import { timeOfDay } from "./server-strip.tsx";
import { ArrangedBar } from "./bar/arranged.ts";
import { BarArranging } from "./bar/arranging.ts";
import { barItemOf } from "./bar/item-slot.ts";
import { pageKeys } from "./controls/page.ts";
import { ownClock, ownNow } from "./own-clock.ts";

/**
 * Game page: `/game?token=<dev token>&server=ws://host:port/game&latency=<ms>&world=<id>&base=<origin>&pointer=current`.
 * Defaults come from `config.json` beside the page (ADR-039): the Pages build writes every one, and the dev
 * server's names only the feedback endpoint and the pointer; a default it leaves out is `aethervale` for
 * `world`, this origin's `/dist` for `base` and port 7000 of this host for `server`, or the developer world's own
 * port of it for a developer world such as `sandbox` (7001, ADR-080). The world loaded is the
 * version the config's pointer names, `current` (the newest compile, which `dev:game` runs) from the dev
 * server and `published` from the Pages build or with no config; `pointer=current` or `pointer=published`
 * picks either (`world-pointer.ts`, ADR-050 amendment of 2026-09-26).
 * Query parameters override every default but `server`, which only a page served from `localhost` or
 * `127.0.0.1` honours, since the gateway is where the page sends its credentials (`endpoint.ts`); `zoom` is
 * an absolute world zoom for development, which otherwise is the default for the window plus the viewer's
 * step (`world-zoom.ts`). F8 in the game toggles the collision debug view (blocked cells and the feet box).
 *
 * The page needs WebGL and says so in a sentence instead of starting without it (`webgl.ts`). It sets the
 * overlay root's scale from the window's device size at the viewer's render resolution (`viewport.ts`,
 * `render-resolution.ts`, `overlay.ts`, CLIENT.md section 7; `render=` in the benchmark mode overrides it) and
 * makes the input router (`ui/input/router.ts`, CLIENT.md section 7.3) before anything else, then shows the
 * loading shell (P1.4-T5, `loading.tsx`), which holds the keyboard and shows real progress over every step
 * the page waits on from then until the world is revealed: the configuration, signing in when the page has
 * no session it can present at once, and the scene's own steps (`scene.ts`, `world-boot.ts`). The shell
 * steps aside while the login screen is up. A failure shows on it with Retry, which asks the scene to retry
 * what failed; with Log in for a token the server refused for an account session, which the tab has
 * forgotten; and with Reload for a stale page. After the reveal, a close, or a sign-in that
 * ends, takes the player out of the world (P1.9-T15): the HUD, the chat and the menu hide, and the shell
 * comes back over them with the disconnect notice (`disconnect.ts`), whose Reconnect joins again through the
 * shell reusing the loaded world, whose Reload reloads, and whose Log in returns to the log in form; nothing
 * reconnects by itself, and the message line never shows a close. The HUD (P1.9-T13, `hud/`) is mounted before
 * the login, under the shell: a store the page and the scene write into as things happen, which tells the
 * HUD only when a value changes, so nothing is drawn on the animation frame; the diagnostic strip shows only
 * when `?diag` asks for it on a development page (`isDevelopmentPage`). Until the world is revealed the HUD is inert, so neither the keyboard nor
 * assistive technology reaches what the shell covers. The page's log is recorded from its first line
 * (`feedback/page-log.ts`, imported before anything else), for a report. The game menu (P1.9-T14,
 * `menu/`) opens on Escape with no layer open, or on the HUD's menu button, once the world is revealed:
 * before that the shell holds the keyboard, and the router's report of Escape, which the report still
 * counts, opens nothing. Its Log out ends the tab's session through the gateway and reloads the page onto
 * the log in form; Log out everywhere, in its Settings' Account group, asks through the confirm dialog first
 * (`logout-everywhere.tsx`, also the name step's). Its Settings moves the world zoom and the viewer's Nameplates choices (P1.9-T24), which
 * the page reads from this browser's storage as it loads and puts on the nameplates' report, which the plates
 * follow (`nameplate-settings.ts`). A press on the world takes focus from a text field (`ui/input/world-press.ts`),
 * since Phaser cancels the press and the browser would otherwise leave the field with the keyboard. The chat
 * panel (P1.9-T23, `chat/`) sits in the HUD's chat slot once the gateway speaks chat, a root of its own under the
 * HUD's; Enter, while the game owns the keyboard, opens its input, which then owns every key. The reading panel
 * (P1.9-T37, `reading/`) shows a sign's text in a root of its own after the HUD's, as a layer of the router that E,
 * Enter and Escape close, so the Enter that closes it opens no chat input. Each Local line
 * also goes to the report's queue, from which the chat bubbles show it over its speaker, and the chat's stepper
 * suggests names from the players the page draws (`drawnPlayerNames`), the player's own left out. Its `/ping` times the
 * network with `GET /ping` on the gateway's HTTP origin (`chat/probe.ts`). A page served from `localhost` or
 * `127.0.0.1` also exposes `window.gameCovering(x, y)`, the read-only report of the pieces that would cover a
 * character standing at a feet point (ADR-071 decision 9, `cover.ts`), and `window.gameFading(x, y)`, each piece's
 * alpha once the occluder fade has run for a character there (decision 10), which the editor's stand-in is
 * compared with.
 *
 * The page sets its text in the game's three families, declared in `ui/generated/fonts.css` and bundled
 * with the page, so it asks no third-party origin for a font (ADR-061, "three faces, self-hosted").
 *
 * Without `?token=` the page logs in through the gateway's account endpoints (P1.4-T2) and presents the
 * session token in `hello`, keeping it fresh from the tab's refresh token; when the gateway refuses that token,
 * as it does once the session is revoked, the tab forgets its grant, so the next reload reaches the log in
 * form. On a development page `?token=` still takes a Phase 0
 * dev token straight through, which is what the browser tests and local links use.
 *
 * The page reads its query only when it is a development page (`page-query.ts`, P1.9-T143): on the published page
 * every parameter named in this file is dropped before it is read, the token among them, so no link changes what the
 * page does or whose session it plays.
 */

/**
 * Reads `config.json` beside the page, if present.
 *
 * @returns The defaults, or null when the file is absent or malformed.
 */
async function loadConfig(): Promise<{ server: string; base: string; world: string; requests: string; benchReports: string; pointer: string; machine: string; status: string } | null> {
  try {
    const response = await fetch(new URL("config.json", window.location.href));
    if (!response.ok) {
      return null;
    }
    const parsed: unknown = await response.json();
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    const { server, base, world, requests, benchReports, pointer, machine, status } = parsed as { server?: unknown; base?: unknown; world?: unknown; requests?: unknown; benchReports?: unknown; pointer?: unknown; machine?: unknown; status?: unknown };
    return { server: typeof server === "string" ? server : "", base: typeof base === "string" ? base : "", world: typeof world === "string" ? world : "", requests: typeof requests === "string" ? requests : "", benchReports: typeof benchReports === "string" ? benchReports : "", pointer: typeof pointer === "string" ? pointer : "", machine: typeof machine === "string" ? machine : "", status: typeof status === "string" ? status : "" };
  } catch {
    return null;
  }
}

/**
 * The loader of the trial look the address asks for (P1.9-T88), called only on a page at a local address
 * (`trial-gate.ts`). The look's code is a dynamic import, so a page that wears none never runs it.
 *
 * @param query The page's query.
 * @returns The loader the world boot calls, or null when `?look=` names no look this page knows.
 */
async function trialLookLoader(query: URLSearchParams): Promise<OwnLookLoader | null> {
  const trial = await import("./trial-look.ts");
  const request = trial.trialLookRequest(query);
  return request === null ? null : (base) => trial.loadTrialLook(base, request);
}

const lacks = browserLacks();
if (lacks.length > 0) {
  throw new Error(`this browser is below the page's floor (it lacks ${lacks.join(", ")}), so the game does not start`);
}
if (!webglAvailable()) {
  showWebglMissing(document.getElementById("webgl-missing") as HTMLElement);
  throw new Error("WebGL is not available, so the game does not start");
}

/**
 * The page's defaults, read before anything else is decided, since `config.json` carries the machine name that tells
 * a development page opened at that name.
 */
const config = await loadConfig();

/**
 * Whether this is a development page (`isDevelopmentPage`): the only kind that reads its query (`page-query.ts`,
 * P1.9-T143), reads the diagnostic strip's lines and exposes the report, the sound's report and the Phaser game on the
 * window, since each tells who is near. The verdict comes from the page's host and its own `config.json`; nothing in
 * the address's query has a say in it.
 */
const developmentPage = isDevelopmentPage(window.location.host, config === null ? "" : config.machine);

/**
 * The status watcher's origin as the page's configuration names it (ADR-078's amendment of 2026-10-13, P1.13-T3): the
 * entry screens' probe asks it for the operator's notice when the gateway gives no answer. Empty on a page given
 * none, which asks no one but the gateway.
 */
const statusWatcher = config === null ? "" : config.status;

/**
 * The page's query: as written on a development page, and empty of every development parameter on any other.
 */
const params = readPageQuery(developmentPage);
const token = params.get("token") ?? "";
readLightingQuery(gameReport.lighting, params);

const host = document.getElementById("game") as HTMLElement;
const overlay = document.getElementById("ui") as HTMLElement;

/**
 * The render resolution (ADR-093 decision 10): the stored divisor resolved against what the window offers, and
 * the view the overlay, the HUD, both scales and the canvas's backing store are laid out from.
 */
const render = new RenderResolution(measureWindow(), readRenderDivisor(), null, undefined, readInterfaceStep());

/**
 * The frame limit (ADR-093 decisions 40 to 45): the stored choice, 60 until the viewer chooses. The benchmark's mode
 * runs with Match display, whatever the device keeps, and neither keeps nor shows a choice, so the default changes
 * none of the figures its reports and the timing suites built on it record.
 */
const benchPage = benchAllowed(params.get("bench"), developmentPage);
const frames = benchPage ? new FrameLimiter("match", null) : new FrameLimiter(readFrameLimit());

let view = render.view;
let started: Phaser.Game | null = null;
const zoom = new WorldZoom(render.device, readZoomStep(), parseZoomOverride(params.get("zoom")), undefined, render.divisor);
const nameplates = new NameplateSettings(gameReport.nameplates, readNameplateChoices());
const idleMarks = new IdleMarksSetting(gameReport.nameplates, readIdleMarks());
const uiScale = new UiScale(view);
const safeInsets = readSafeInsets(document, view.ratio ?? 1);
const hud = new HudStore(hudLayout(view, safeInsets));

/**
 * The ability bar's, the cast bar's and the effects rows' store and its per-frame writer (P2.2-T4), which the game's step
 * drives with each frame's time once the game is made.
 */
const bar = new BarStore(PAGE_RULES);
const drenchedForce = developmentPage ? parseForcedDrenched(params.get("drenched")) : null;
const drenchedNumber = effectNumber(PAGE_RULES, "drenched");
if (drenchedForce !== null) {
  bar.forceOwnEffect({ effect: drenchedNumber, ticksLeft: Math.round(drenchedForce * (PAGE_RULES.effects.get("drenched")?.durationTicks ?? 0)), stacks: 1 });
}
const barTicker = new BarTicker(bar, null, true);

/**
 * The weapon's stance (P2.2-T13), which the session's fight moves and the HUD's stance button draws and toggles.
 */
const stance = new StanceStore();

/**
 * The bar's and the effects rows' tooltips (P2.2-T12): which one shows, by hover, focus, long press or a click on an
 * effect.
 */
const tips = new TipController();

/**
 * The player's bag (P2.8-T11, ADR-091): what the server last said of it, which the Inventory window draws.
 */
const bag = new BagStore(PAGE_RULES);

/**
 * The bar the player arranges (P2.8-T29, ADR-121): the server's record, written down at the first change from the default
 * bar, whose item slot shows the bag's first consumable; nothing is arranged while the account is frozen.
 */
const arranged = new ArrangedBar(PAGE_RULES, () => barItemOf(bag.getSnapshot(), PAGE_RULES)?.item ?? null, () => moderation?.feed.frozen() ?? false);

/**
 * The tool belt (ADR-142 decision 3b): the server's record of the four tool slots and the one in hand, which Q and the
 * Tool keys move, a drag from the bag fills, the HUD draws and harvesting reads; nothing changes while the account is frozen.
 */
const belt = new ToolBelt(PAGE_RULES, () => moderation?.feed.frozen() ?? false);

/**
 * The player's companion (ADR-142 decision 6): whether he has joined, is out, and his name, as the server's `companionState` says; nothing
 * is asked while the account is frozen.
 */
const companion = new CompanionStore(() => moderation?.feed.frozen() ?? false);

/**
 * The player's wallet (P2.8-T38, ADR-124 decision 4): what the server last said the character holds, which the
 * Inventory window's footer draws.
 */
const wallet = new WalletStore();
const character = new CharacterStore(PAGE_RULES.constants.attribute_cap);
character.bindSends({ spend: (spends) => scene?.connection?.sendSpendAttributes(spends) ?? false, respec: () => scene?.connection?.sendRespecAttributes() ?? false, companionSet: (out) => (moderation?.feed.frozen() ?? false ? false : (scene?.connection?.sendCompanionSet(out) ?? false)) });
companion.subscribe(() => character.setCompanions(companionViews(companion.getSnapshot())));

/**
 * The chest the loot window shows (P2.8-T16, ADR-091 decisions 19 and 22): what the server last said of the chest the
 * player opened; the toasts of what a take moved to the bag; and the order of the page's two play windows, by which the
 * one opened or pressed last is drawn in front and closed first by Escape.
 */
const chest = new ChestStore();
const lootToasts = new LootToasts(
  (fn, ms) => {
    const timer = setTimeout(fn, ms);
    return () => clearTimeout(timer);
  },
  () => performance.now(),
);
const windowOrder = new WindowOrder();
/**
 * The page's icons (ADR-111): the pointer and its atlas, loaded once beside the sounds when the asset base is known and
 * held for the page's life; the HUD draws glyphs until the atlas has decoded. The UI scale they are chosen by follows
 * the overlay's.
 */
const icons = PAGE_ICONS;
const healthNumbers = new HealthNumbersSetting(hud, readHealthNumbers());

/**
 * Whether the ability bar's slots draw their keys (P2.18-T15): off unless this device chose them, kept per device.
 */
const barKeys = new BarKeysSetting(hud, readBarKeys());

/**
 * Whether other players' emoji are drawn over them (P2.19-T7, ADR-114 decision 5.8): on unless this device turned them
 * off, kept per device; the player's own always draw.
 */
const emotesOthers = new EmotesOthersSetting(gameReport.emoji, readEmotesOthers());

/**
 * The performance panel (ADR-087): shown by F9 or Settings, kept per viewer; it reads the world scene and the current
 * connection once the page has made them.
 */
const perf = new PerfMonitor({ link: () => scene?.connection?.perf ?? null, counts: () => sceneCounts(scene), now: () => performance.now() }, { shown: readPerformance(), store: localStore() });
mirrorReducedMotion(PAGE_MOTION, hud);
/**
 * The page's moderation (ADR-056's amendment of 2026-10-04), made once the gateway's address is known; until then a
 * GM command is no command.
 */
let moderation: ModerationPage | null = null;

/**
 * The `/badge` command (ADR-112 decision 5.5), made with the moderation once the gateway's address is known; until then, and
 * until the gateway sends `staffBadgeOffer`, the chat holds no such command. It presents the account session's token; a page joined by `?token=`
 * (the browser suites' rigs) has no account session and presents the token it joined with, which the gateway checks as it
 * checks any.
 */
let badgeCommands: BadgeController | null = null;
const partyStore = new PartyStore({ line: (text) => chatStore.note(text), self: () => gameReport.name });
gameReport.nameplates.party.set(partyStore.getSnapshot().members);
partyStore.subscribe(() => gameReport.nameplates.party.set(partyStore.getSnapshot().members));
partyStore.subscribe(() => hud.setInParty(partyStore.getSnapshot().members.length > 0));
let partyVitalsSeen: { readonly vitals: unknown; readonly flag: number } | null = null;
hud.subscribe(() => {
  const held = hud.getSnapshot();
  partyStore.setInCombat(invitationsHeld(held.vitals));
  if (partyVitalsSeen === null || partyVitalsSeen.vitals !== held.vitals || partyVitalsSeen.flag !== held.flag) {
    partyVitalsSeen = { vitals: held.vitals, flag: held.flag };
    partyStore.setOwnVitals(ownVitalsOf(held.vitals, held.flag));
  }
});
const chatStore = new ChatStore(hudLayout(view, safeInsets), { menu: (who, x, y) => partyStore.openMenu({ firstName: who.firstName, familyName: who.familyName, x, y }), party: (request) => partyStore.request(request), onLocal: (line) => gameReport.bubbles.say(line), inView: () => (scene === null ? [] : drawnPlayerNames(gameReport.players, scene.characters)), self: () => gameReport.name, badgeOf: (handle) => scene?.badgeOf(handle) ?? 0, cardOf: (full) => (scene === null ? null : cardFactsOf(full, gameReport.players, scene.characters, (handle) => scene?.cardFactsOf(handle) ?? null)), standing: partyStore.standing, moderation: (ask) => moderation?.ask(ask) ?? false, badge: (ask) => badgeCommands?.ask(ask) ?? false });
partyStore.subscribe(chatStore.standingChanged);
const barPaging = new BarPagingSetting([hud, chatStore], readBarPaging());
applyOverlayScale(overlay, view);
icons.setScale(uiScaleOf(view));
applyCanvasSize(host, view, render.divisor);
render.subscribe(() => {
  view = render.view;
  applyOverlayScale(overlay, view);
  icons.setScale(uiScaleOf(view));
  applyCanvasSize(host, view, render.divisor);
  const laid = barPaging.laidOut(hudLayout(view, readSafeInsets(document, view.ratio ?? 1)));
  hud.setLayout(laid);
  chatStore.setLayout(laid);
  zoom.resize(render.device, render.divisor);
  uiScale.resize(view);
  started?.scale.resize(view.width, view.height);
});
watchDevice(host, (next) => render.resize(next));

/**
 * The input router, made before anything is shown, so the loading shell and the login panel's confirm
 * dialog hold the keyboard on it, and before the game, so its window listeners run before Phaser's keyboard.
 */
const input = new InputRouter({ target: window, isTextField, focused: () => document.activeElement });
const touch = mountTouchControls(overlay, input, () => scene);
const stick = touch.stick;

/**
 * Whether the ability bar is locked on this device (ADR-121 decision 3.2), which Settings, Controls changes.
 */
const barLock = { locked: readBarLocked() };

/**
 * Arranging the bar (P2.8-T29): drags from the bar and the bag, and the keyboard's placing on the input router. A slot
 * of the bag picked up (Ctrl+Enter, or the item menu's Place on bar) is placed by a press on a bar slot.
 */
const barArranging = new BarArranging(arranged, PAGE_RULES, () => barLock.locked, input, bar.pages, belt);

/**
 * Lock the ability bar as Settings, Controls reads and changes it, kept per device.
 */
const barLockSetting = {
  get locked(): boolean {
    return barLock.locked;
  },
  setLocked: (locked: boolean): void => {
    barLock.locked = locked;
    writeBarLocked(locked);
  },
};

/**
 * Reset ability bar as Settings, Controls offers it: an arranged bar, once its session's bar arrived and while the
 * account is not frozen.
 */
const barReset = {
  canReset: (): boolean => arranged.canArrange() && arranged.getSnapshot().record !== null,
  reset: (): boolean => arranged.resetToDefault(),
};
barArranging.bindBag({
  held: () => {
    const { selected, slots } = bag.getSnapshot();
    return selected === null ? null : (slots[selected]?.item ?? null);
  },
  putBack: () => void bag.putBack(),
});
suppressGameContextMenu(window, isTextField);

/**
 * Escape closes an open tooltip before anything else the game does with it (ADR-061's amendment of 2026-10-02): this
 * listens before the scene's, which clears a target.
 */
input.onEscape(() => tips.escape());
(window as unknown as { inputRouter: InputRouter }).inputRouter = input;
Object.defineProperty(gameReport, "keyboard", { get: () => input.owner, enumerable: true, configurable: true });

/**
 * The page's sound (ADR-085): the first press unlocks it, the window's visibility mutes it in the background, a
 * control pressed clicks, the death screen's `player_death` cue plays the death, and the entry screens' music is
 * asked for until the world is shown. The sound's report is `window.gameAudio` from the first screen on, since
 * `window.game` is made only with the world and the entry screens play sound too (the Voice choice's Preview,
 * P1.9-T83); a page that is not a development page takes it off the window once `config.json` is read, before any
 * world is joined, since in the world its log names other players' footsteps and blows.
 */
pageSound.attach(window, document);
PAGE_CUES.on("player_death", () => pageSound.cues.died());
gameReport.audio = pageSound.report;
(window as unknown as { gameAudio: typeof pageSound.report }).gameAudio = pageSound.report;
pageSound.scene(MusicScene.Title);

/**
 * The chat alert (ADR-085's amendment "the chat alert"): a whisper received, or a line another player wrote that
 * mentions the player's character name or family name, sounds `sfx/ui/chat/alert` on UI, at most once per
 * `CHAT_ALERT_INTERVAL_MS`. It reads the names the report holds and the block list and channels the chat holds.
 */
const chatAlerts = new ChatAlerts({
  now: Date.now,
  context: () => ({ self: gameReport.name, blocks: chatStore.getSnapshot().blocks, channels: chatStore.getSnapshot().channels }),
  play: (kind) => pageSound.chatAlert(kind),
});

/**
 * The diagnostic strip's feed, only when the page asks for the strip with `?diag`. Lines are read into it only on a
 * development page, which the page knows once `config.json` is read; on any other page the feed stays empty and the
 * strip draws nothing.
 */
const diagnostics = params.has("diag") ? new DiagnosticsFeed() : null;

/**
 * The game menu once the page has made it, after the login, when it knows whether it plays an account.
 */
let menu: GameMenuControl | null = null;
const hudHost = document.getElementById("hud") as HTMLElement;
const warnings = new MaintenanceWarnings({ now: Date.now, clock: timeOfDay, sound: () => pageSound.ui(InterfaceSound.Maintenance) });
const levelUp = new LevelUpToast((fn, ms) => {
  const timer = setTimeout(fn, ms);
  return () => clearTimeout(timer);
});
const discoveries = new DiscoveryToasts((fn, ms) => {
  const timer = setTimeout(fn, ms);
  return () => clearTimeout(timer);
});
/**
 * The death screen's state, made before the HUD so the emote wheel can read it; it is mounted further down.
 */
const death = new DeathScreen((name) => PAGE_CUES.fire(name));

/**
 * The emote wheel (ADR-114): its store, its keys and its mouse wheel, bound through the input router. H opens it only
 * for a character in the world, alive and not frozen, once the gateway has offered emotes, and never over a panel
 * that names a key; the Inventory and loot windows' layers name none and do not count. The HUD draws it and the game
 * menu's Emotes item opens it.
 */
const emoteWheel = startEmoteWheel({
  input,
  events: window,
  settings: localStore(),
  hud,
  death,
  frozen: () => moderation?.feed.frozen() ?? false,
  playWindows: () => (bag.getSnapshot().open ? 1 : 0) + (chest.getSnapshot() === null ? 0 : 1),
  world: () => started?.canvas ?? null,
});
/**
 * The Nearby window's saved flag is forgotten (P2.12-T22, ADR-100 decision 44): the Map opens on purpose and nothing
 * reads `odyssey.minimap.expanded` again.
 */
forgetMinimapExpanded(localStore());
const minimap = new MinimapStore(localStore());

/**
 * The world map (P2.12-T6, ADR-100): the window M opens, the page's copy of exploration and the places the server sent,
 * and the picture of the map the scene draws a chunk of a frame while the window is open. The pin is kept on this
 * device for the character the page plays, which is the account session's.
 */
const worldMap = new WorldMapStore({ character: () => current?.characterId ?? "dev", nearby: { step: () => minimap.getSnapshot().step, setStep: (step) => minimap.setStep(step), subscribe: minimap.subscribe } });
gameReport.worldMap = () => worldMap.report();
worldMap.onPlaces((places) => minimap.setPlaces(placeSpotsOf(places)));
const partyPositions = new PartyPositionsBook();
bindPartyOnMap(partyPositions, partyStore, { minimap, worldMap });
const mapPopout = new MapPopout(browserPopout(window), localStore());

/**
 * Waypoint travel (ADR-137 decisions 22 and 23, P2.11-T38): an offer opens the world map at Region on the character in
 * travel mode, docked as the window (a popped-out or floating Map comes back, since the offer lives on this page), and
 * its closing discards the offer; the channel shows its bar in the cast slot, Escape cancelling it through a layer of
 * the input router; refusals and breaks say why on the message line.
 */
const travel = new TravelStore({
  sendTravelAsk: () => scene?.connection?.sendTravelAsk() ?? false,
  sendTravel: (serial, index) => scene?.connection?.sendTravel(serial, index) ?? false,
  sendTravelCancel: () => scene?.connection?.sendTravelCancel() ?? false,
  tick: () => scene?.connection?.view.tick ?? 0,
  openMap: () => {
    const shown = worldMap.getSnapshot();
    if (shown.open && shown.presentation === "popped") {
      worldMap.popIn();
    } else if (shown.open && shown.presentation === "float") {
      worldMap.dock();
    }
    const you = worldMap.getSnapshot().you;
    if (you === null) {
      worldMap.open();
    } else {
      worldMap.openAt({ x: you.x, y: you.y });
    }
  },
  closeMap: () => worldMap.close(),
  mapOpen: () => worldMap.getSnapshot().open,
  onMapChange: (listener) => worldMap.subscribe(listener),
  say: (line) => sayOnHud(hud, line),
  openLayer: (onClose) => input.open({ onClose }),
  now: () => performance.now(),
});
gameReport.travel = () => travel.getSnapshot();

/**
 * Harvesting grass (ADR-142 decision 3, P2.26-T3): the props the server says are gone fade out and come back on the
 * canvas, E harvests the prop in reach while a sickle is in hand, and the hold shows its bar in the cast slot.
 */
const harvest = new HarvestStore({
  send: (index) => scene?.connection?.sendHarvest(index) ?? false,
  tick: () => scene?.connection?.view.tick ?? 0,
  now: () => performance.now(),
  say: (line) => sayOnHud(hud, line),
  withhold: (id, withheld, fadeMs) => scene?.withholdProp(id, withheld, fadeMs),
});
gameReport.harvest = () => harvest.reading();
gameReport.belt = () => {
  const { arrived, record } = belt.getSnapshot();
  return { arrived, slots: record.slots, selected: record.selected };
};
mountHud(hudHost, hud, {
  warnings,
  levelUp,
  discoveries,
  onLogOut: () => void logOut(),
  diagnostics,
  onCommit: () => {
    gameReport.hudRenders += 1;
  },
  onMenu: () => menu?.request(),
  onInteract: () => scene?.interactFromUi(),
  onFlagToggle: (wanted) => hud.requestFlag(wanted),
  onFlagNotice: (line) => sayOnHud(hud, line),
  onFlagPanel: (open) => {
    hud.setFlagPanel(open);
  },
  bar: { source: bar, ticker: barTicker, tips, arrange: barArranging },
  stance,
  perf,
  bag: { source: bag, rules: PAGE_RULES, takeEntry: () => bag.takeEntry(), onUse: (slot) => void bag.use(slot), move: bag, input, onClose: () => bag.setOpen(false), onToggle: () => bag.toggle(), order: windowOrder, wallet, arrange: barArranging },
  character: { source: character, rules: PAGE_RULES, onClose: () => character.setOpen(false), order: windowOrder, input, portraitOf: () => null },
  chest: { source: chest, rules: PAGE_RULES, takeEntry: () => chest.takeEntry(), onTake: (line) => void chest.take(line), onTakeAll: () => void chest.takeAll(), onClose: () => chest.close(), order: windowOrder },
  loot: { source: lootToasts, rules: PAGE_RULES },
  emotes: emoteWheel.hud,
  minimap: { source: minimap, position: minimap, onMove: (listener) => minimap.onMove(listener), ground: () => minimap.pictures, others: minimap.others, pin: () => minimap.pin(), party: () => minimap.partySpots(), places: () => minimap.places(), placeCount: () => minimap.compassPlaces(), onZoomIn: () => minimap.zoomIn(), onZoomOut: () => minimap.zoomOut(), mapButton: { source: worldMap, toggle: () => worldMap.toggle(), openAt: (point) => worldMap.openAt(point) }, onMapField: (size) => minimap.setMapField(size) },
  worldMap: { store: worldMap, popout: mapPopout, travel },
  harvest,
  party: {
    source: partyStore,
    input,
    menu: {
      chat: chatStore,
      actions: { whisper: chatStore.startWhisper, block: (family) => void chatStore.submit(`/block ${family}`), unblock: (family) => void chatStore.submit(`/unblock ${family}`) },
    },
  },
  icons,
});

/**
 * The world map's keys (ADR-100 decision 1): M opens and shuts the window, Escape closes it and C returns the view to
 * the character while it is open, through the input router, so never while a text field has focus; every other key
 * still reaches the game.
 */
bindWorldMapKeys(worldMap, input);

/**
 * The bag's keys (ADR-091 decision 21): I opens and closes the Inventory window and Escape closes it, through the
 * input router, so never while a text field has focus; every other key still reaches the game. The report follows
 * the bag.
 */
bindBagKeys(bag, input, tips, windowOrder);
bindCharacterKeys(character, input, windowOrder);

/**
 * The swap control's key (P1.9-T133): on a paged ability bar, the key Swap bar page is bound to shows the other five
 * slots, through the input router, so never while a text field has focus.
 */
bindPageKeys(bar.pages, input);
bag.subscribe(() => {
  gameReport.bag = bag.getSnapshot();
});
wallet.subscribe(() => {
  gameReport.wallet = wallet.getSnapshot();
});

/**
 * The loot window's keys (ADR-091 decision 22): Escape closes it and Shift+E enters it, through the input router;
 * every other key still reaches the game. The report follows the chest.
 */
bindChestKeys(chest, input, windowOrder);
chest.subscribe(() => {
  gameReport.chest = chest.getSnapshot();
});

/**
 * The flag panel's keys (ADR-076's amendment of 2026-10-01): U opens the flag toggle's panel, U again or Enter presses
 * its button and Escape closes it, through the input router, so never while a text field has focus.
 */
bindFlagPanelKeys(hud, input, ownNow, () => (bag.getSnapshot().open ? 1 : 0), undefined, (line) => sayOnHud(hud, line));

/**
 * F9 shows and hides the performance panel (ADR-087 decision 3), through the input router.
 */
bindPerfKey(perf, input);
hud.subscribe(() => {
  gameReport.message = hud.getSnapshot().message;
  gameReport.vitals = hud.getSnapshot().vitals;
});

/**
 * The death screen (ADR-073's amendment "the respawn is the player's, and a death is said in chat"), in the first host
 * of the overlay, under the chat and the HUD, firing the page's cues; its keys listen before the chat's Enter, so
 * Enter and Space press its button once enabled and open nothing else. The report reads it, saying the button ready
 * as the overlay draws it.
 */
const deathHost = document.getElementById("death") as HTMLElement;
mountDeath(deathHost, death);
input.onKey(deathKeys(death, () => performance.now()));
Object.defineProperty(gameReport, "death", {
  get: () => {
    const shown = death.getSnapshot();
    return { shown: shown !== null, fade: shown?.fade ?? false, cause: shown?.cause ?? null, costs: shown?.costs ?? [], place: shown?.place ?? null, ready: shown !== null && death.drawnReady, cues: PAGE_CUES.count("player_death") };
  },
  enumerable: true,
  configurable: true,
});

/**
 * The chat panel (P1.9-T23), in its own host under the HUD's, and Open chat's key (Enter unless the player chooses
 * another, P1.9-T174), while the game owns the keyboard, opening its input once the world is shown and the connection
 * speaks chat. The key on a focused control (the Send button, the menu button, a link) keeps its own meaning and opens
 * nothing, and a press another listener took (the death screen's button) opens nothing.
 */
const chatHost = document.getElementById("chat") as HTMLElement;
const chatSize = new ChatSizeSetting(readChatSize(localStore()));
const chatText = new ChatTextSetting(readChatText(), document.documentElement);
const chat = mountChat(chatHost, chatStore, input, chatSize, PAGE_ICONS);
input.onKey((event) => {
  const control = event.target instanceof Element && event.target.closest("button, a[href], [role='menuitem'], [role='option']") !== null;
  if (pageKeys.actionOf(event) === "openChat" && !event.repeat && !event.defaultPrevented && !control && chat.open()) {
    event.preventDefault();
  }
});

/**
 * The reading panel (P1.9-T37), in its own host after the HUD's, which the scene opens with a granted reply's text
 * and closes when its thing leaves the character's reach; it is a layer of the router, so the world, the chat and the
 * HUD run on under it. The report follows it.
 */
const readingHost = document.getElementById("reading") as HTMLElement;
const reading = new ReadingStore();
mountReading(readingHost, reading, input);
reading.subscribe(() => {
  const shown = reading.getSnapshot();
  gameReport.reading = { open: shown !== null, handle: shown?.handle ?? -1, text: shown?.text ?? "", layout: shown?.layout ?? "scroll", opened: shown?.serial ?? gameReport.reading.opened };
});

/**
 * The dialogue window (ADR-136 decision 5.6), in its own host after the reading panel's, which a `dialogue` opens and
 * the server's `dialogueClose` or the player's Leave closes; it is a layer of the router, so the world, the chat and
 * the HUD run on beside it. A speaker is named and drawn from the loaded world's NPC look table.
 */
const dialogueHost = document.getElementById("dialogue") as HTMLElement;
const dialogue = new DialogueStore();
mountDialogue(dialogueHost, dialogue, input, { name: (speaker) => scene?.npcName(speaker) ?? "", portrait: (speaker) => scene?.npcPortrait(speaker) ?? null }, icons);

/**
 * Listens for what the game menu shows of the emote wheel changing: the gateway's offer, and a freeze coming or
 * going.
 *
 * @param listener Called when it may have changed.
 * @returns A function that stops listening.
 */
function watchEmotesOffer(listener: () => void): () => void {
  const unsubscribe = emoteWheel.store.subscribe(listener);
  const unwatch = moderation?.watchFreeze(listener) ?? ((): void => undefined);
  return () => {
    unsubscribe();
    unwatch();
  };
}

/**
 * The scene once the page has made it, which Retry asks to retry what failed.
 */
let scene: GameScene | null = null;

/**
 * Whether the page plays an account session, whose refused token means logging in again.
 */
let account = false;

/**
 * Acts on a notice's button (`notice-action.ts`).
 *
 * @param failure The failure shown.
 */
function act(failure: LoadingFailure): void {
  actOnNotice(failure.action, { reload: () => window.location.reload(), logIn, retry: scene === null ? null : () => scene?.retry() });
}

/**
 * The notice's Log in: stops keeping the session fresh and forgets the tab's grant, as the game menu's Log
 * out does, then reloads, so the page finds no grant and shows the log in form. It reloads rather than showing
 * the login screen in place, since the scene, the connection and the world's load would all have to be undone
 * first (P1.9-T14).
 */
function logIn(): void {
  stopFresh?.();
  storeGrant(null);
  window.location.reload();
}

const kept = token === "" ? loadGrant() : null;
const signingIn = token === "" && !(kept?.kind === "playing" && kept.session.expiresAt > Date.now());
const shell = mountLoadingShell(document.getElementById("loading") as HTMLElement, { steps: gameStartSteps(signingIn), holds: input, onRetry: act, queue: { name: () => (current === null ? "" : hudName(current).firstName), onLeave: () => void leaveQueue() } });

/**
 * The configuration is read before the shell is mounted, since the shell's steps depend on the token and the token on
 * the verdict `config.json` completes. The step stays in the shell's list, started and finished at once, so the
 * count of steps and the bar's share for each are what they were. Until `config.json` answers, one request to the
 * page's own origin, the page shows its background alone.
 */
shell.dispatch({ type: "started", step: "configuration", total: 1 });
shell.dispatch({ type: "finished", step: "configuration" });
const worldId = params.get("world") ?? (config !== null && config.world !== "" ? config.world : "aethervale");
const requestedServer = params.get("server");
const url = gatewayUrl(window.location.hostname, requestedServer, config === null ? "" : config.server, worldId);
if (requestedServer !== null && !isLocalPage(window.location.hostname)) {
  console.warn("?server= is honoured only on a page served from localhost or 127.0.0.1; this page talks to its configured gateway");
}
const gatewayOrigin = authBase(url);
const versionHint = keptVersionHint();
chatStore.bindProbe(gatewayOrigin === "" ? null : httpProbe(gatewayOrigin));
badgeCommands = new BadgeController({ change: badgeClient({ endpoint: `${gatewayOrigin}/staff/badge`, token: () => current?.sessionToken ?? (account ? null : presented) }), note: (text) => chatStore.note(text), fail: (text) => chatStore.fail(text), sessionless: () => !account });
moderation = startModeration({ host: document.getElementById("moderation") as HTMLElement, input, endpoint: `${gatewayOrigin}/moderation`, staffEndpoint: `${gatewayOrigin}/staff`, token: () => current?.sessionToken ?? null, note: (text) => chatStore.note(text), fail: (text) => chatStore.fail(text), self: () => (gameReport.name === null || gameReport.name.familyName === "" ? null : gameReport.name.familyName) });
moderation.watchFreeze(emoteWheel.shut);
const latencyMs = Number(params.get("latency") ?? "0");
const base = (params.get("base") ?? (config !== null && config.base !== "" ? config.base : "/dist")).replace(/\/+$/, "");
void pageSound.load(base);
void icons.load(base);
if (!developmentPage) {
  delete (window as unknown as { gameAudio?: typeof pageSound.report }).gameAudio;
}
if (diagnostics !== null && developmentPage) {
  watchDiagnostics(diagnostics, () => diagnosticLine(gameReport, hud.getSnapshot().name, latencyMs));
}

/**
 * The looks the entry screens draw (ADR-094 decisions 23 and 27): the page's table and the look sheets' pointer under
 * the asset base, fetched once when a screen first asks.
 */
const entry = entryLooks(PAGE_RULES.looks, base, window.matchMedia("(pointer: coarse)").matches);
/**
 * The page's confirm dialogs over the page on the input router: before logging out everywhere (P1.4-T15), and the
 * questions character select's Log out and the game menu's Change character ask first.
 */
const confirms = mountConfirms(document.getElementById("modal") as HTMLElement, input);
const confirmEverywhere = confirms.everywhere;

/**
 * The dialog for a logout that did not finish, over every other layer (ADR-075 decision 6).
 */
const retryLogout = mountLogoutRetry(document.getElementById("logout-retry") as HTMLElement, input);

/**
 * The refresh token this tab holds, which another tab's logout makes the page check with the gateway: the
 * login screen's while it is up, then the playing session's (ADR-075 decision 6).
 */
const held: HeldToken = { refreshToken: null };
if (token === "") {
  watchLogoutElsewhere(authBase(url), held, () => window.location.reload());
}

/**
 * The session to play with, and the token to present. A `?token=` is taken as given, with no account
 * behind it. Otherwise `admitAccount` finds the account's session: the tab's stored one, a refresh of it, the
 * returning prompt of a kept device, or the login screen, whose login leads through character select or the
 * name step until a character may play. The loading shell counts this as its sign-in step and steps aside
 * while the login screen is up.
 *
 * @returns The token for `hello` and the account session behind it, if any.
 */
async function admit(): Promise<{ readonly token: string; readonly session: Session | null }> {
  if (token !== "") {
    return { token, session: null };
  }
  const session = await admitAccount(authBase(url), shell, { confirmEverywhere, ask: confirms.ask, retryLogout, looks: entry }, held, new StatusPoll({ probe: () => probeStatus(authBase(url), fetch, PROBE_TIMEOUT_MS, statusWatcher) }));
  held.refreshToken = session.refreshToken;
  return { token: session.sessionToken, session };
}

const admitted = await admit();
account = admitted.session !== null;
const named = hudName(admitted.session);
hud.setName(named.firstName, named.familyName);

/**
 * The token the page presents when it connects: the one it was admitted with, then each one the kept-fresh
 * session is given, so a join after a retry presents one that has not expired. A refreshed grant does not
 * rename the HUD: from the welcome on, the vitals frame shows the name the map server holds for the character,
 * which its nameplate draws too (ADR-067 decision 4).
 */
let presented = admitted.token;

/**
 * The account session the tab plays: the one it was admitted with, then each one the kept-fresh session is
 * given, whose refresh token the game menu's Log out and Log out everywhere present. Null for a `?token=`
 * page, which has no session to end. When it can no longer be refreshed (the account logged out everywhere,
 * or the sign-in expired) or the gateway answers that the account needs a name first, the page leaves the
 * world to the sign-in's notice (P1.9-T15), whose Log in or Reload takes the player on; the message line never
 * says it.
 */
let current: Session | null = admitted.session;

/**
 * Keeps an account session fresh (`keepFresh`): each refreshed session becomes the one the page presents and plays,
 * and one that can no longer be refreshed takes the page out of the world to the sign-in's notice.
 *
 * @param session The session to keep fresh.
 * @returns A function that stops keeping it fresh.
 */
function freshen(session: Session): () => void {
  return keepFresh(
    authBase(url),
    session,
    (next) => {
      presented = next.sessionToken;
      current = next;
    },
    (why) => scene?.endSignIn(why),
  );
}

/**
 * The refresh in flight before a join, so two joins that find the token lapsed share one.
 */
let renewing: Promise<FreshToken> | null = null;

/**
 * Makes sure the token a join presents has not lapsed (P1.9-T32): a player who was out of the world for longer than the
 * token lives, because the gateway was down or the connection was, would otherwise present it at `hello`, which ends the
 * sign-in on an expired token as on a revoked one. A token that holds is `ready` at once; a lapsed one is refreshed once,
 * and the page presents the new one, or none when the gateway did not answer. A sign-in the gateway refused to renew stops
 * being kept fresh.
 *
 * @returns `"ready"`, or a promise of what the refresh came to.
 */
function freshToken(): "ready" | Promise<FreshToken> {
  const session = current;
  if (session === null || !lapsed(session, Date.now())) {
    return "ready";
  }
  renewing ??= renewLapsed(authBase(url), session)
    .then((outcome): FreshToken => {
      if (outcome.kind === "ready") {
        presented = outcome.session.sessionToken;
        current = outcome.session;
        return "ready";
      }
      if (outcome.kind === "ended") {
        stopFresh?.();
        stopFresh = null;
        return outcome.why;
      }
      return "failed";
    })
    .finally(() => {
      renewing = null;
    });
  return renewing;
}

/**
 * Stops keeping the session the page plays fresh, or null with none kept: a `?token=` page, or while Change
 * character shows select.
 */
let stopFresh: (() => void) | null = admitted.session === null ? null : freshen(admitted.session);

/**
 * Ends this tab's account session when the gateway refuses its token at `hello`, as it does for a session
 * revoked by logging out everywhere or from another tab: the tab forgets its grant and stops refreshing,
 * so the next reload reaches the log in form rather than presenting the refused token again. The loading
 * shell's notice says so.
 */
function endRefusedSession(): void {
  stopFresh?.();
  storeGrant(null);
}

/**
 * The game menu's Log out (P1.9-T14): stops keeping the session fresh, so no refresh stores a grant behind
 * it, and forgets the tab's grant at once, as the name step's Log out does, so a reload before the gateway
 * answers finds the log in form rather than the session it asked to end; then ends the session through the
 * gateway while the menu shows the item busy, and reloads the page, which finds no grant and shows the log
 * in form. A logout that did not finish says so and offers Retry before the page reloads (ADR-075). Before any of
 * it the page asks the player, in the confirm dialog, since the character stays in the world after it, and then asks
 * the gateway to leave (`leaveByChoice`): a fight refuses it and the page stays as it was;
 * otherwise the grant is forgotten as the gateway takes the leave, and the character stays in the world for the
 * gateway's count, which the page waits out before it ends the session.
 *
 * @returns True as the page starts to reload, false when the player cancelled or a fight refused the leave; it never rejects, since
 *   `logout` answers every failure rather than throwing.
 */
async function logOut(): Promise<boolean> {
  /**
   * Stops keeping the session fresh and forgets the tab's grant.
   */
  const forget = (): void => {
    stopFresh?.();
    stopFresh = null;
    storeGrant(null);
  };
  if (!(await confirms.ask(logoutQuestion()))) {
    return false;
  }
  const leave = chosenLeave();
  const left = await leaveByChoice({
    ...leave,
    stay: (ticks) => {
      forget();
      return leave.stay(ticks);
    },
  });
  if (!left) {
    return false;
  }
  forget();
  const session = current;
  if (session !== null) {
    await logOutWithRetry(authBase(url), session.refreshToken, retryLogout);
  }
  window.location.reload();
  return true;
}

/**
 * What a chosen leave needs from the page (`leave-flow.ts`, ADR-084's amendment of 2026-10-13): the question to the
 * gateway, the menu told of a fight's lock, and the stay's countdown in the page's dialog.
 *
 * @returns The parts.
 */
function chosenLeave(): ChosenLeavePage {
  return {
    askLeave: () => scene?.askLeave(true) ?? Promise.resolve(null),
    locked: (ticks) => menu?.locked(ticks),
    stay: (ticks) => showStay(confirms, ticks).then(() => noteOwnLeave(Date.now())),
  };
}

/**
 * Settings' Log out everywhere (P1.4-T15's action, in the game menu's Settings until P1.4-T32): asks through the
 * confirm dialog, which runs the request and shows busy and the server's sentence. Once every session has
 * ended, or the gateway has answered that this tab's own session was already gone (after which the dialog
 * says so and offers Log in), the tab leaves for the log in form as Log out does.
 *
 * @returns True when the page is leaving, false when the player cancelled and stays.
 */
async function logOutEverywhere(): Promise<boolean> {
  const session = current;
  if (session === null) {
    return false;
  }
  let lost = false;
  const ended = await confirmEverywhere(async () => {
    const outcome = await logoutEverywhere(authBase(url), session.refreshToken);
    if (!outcome.ok && outcome.lost) {
      lost = true;
      storeGrant(null);
    }
    return outcome;
  });
  if (!ended && !lost) {
    return false;
  }
  stopFresh?.();
  window.location.reload();
  return true;
}

/**
 * The page's way back to character select (`change-character.ts`'s `returnToSelect`), which Change character and
 * Leave queue share: departing forgets the tab's grant and what the chat held and waits for the gateway to release
 * the character; select is shown from a refresh of the kept session, with the server's status over it as on every
 * entry screen; and the session chosen there is played, joining again with its token over the world already loaded.
 *
 * @returns The page's parts.
 */
function selectReturn(): SelectReturnPage {
  const base = authBase(url);
  return {
    current: () => current,
    depart: async () => {
      stopFresh?.();
      stopFresh = null;
      storeGrant(null);
      await scene?.depart();
      chatStore.forget();
      partyStore.forget();
    },
    renew: (refreshToken, characterId) => renew(base, refreshToken, characterId),
    choose: async (choosing) => {
      const status = new StatusPoll({ probe: () => probeStatus(base, fetch, PROBE_TIMEOUT_MS, statusWatcher) });
      status.start();
      shell.hide();
      try {
        return await showLogin(base, { choosing, confirmEverywhere, ask: confirms.ask, held, retryLogout, status, looks: entry });
      } finally {
        status.stop();
        shell.show();
      }
    },
    play: (session) => {
      storedVoice = null;
      storedPartyInvites = null;
      forgetOwnLeave();
      storeGrant({ kind: "playing", session });
      held.refreshToken = session.refreshToken;
      current = session;
      presented = session.sessionToken;
      stopFresh = freshen(session);
      const renamed = hudName(session);
      hud.setName(renamed.firstName, renamed.familyName);
      scene?.retry();
    },
    reload: (grant) => {
      storeGrant(grant);
      window.location.reload();
    },
  };
}

/**
 * The game menu's Change character (P1.9-T62, `change-character.ts`): asks first, then returns to character select
 * (`selectReturn`).
 *
 * @returns True once the page has departed the world, false when the player stayed or the page has no account.
 */
function changeCharacter(): Promise<boolean> {
  return runChangeCharacter({ ...selectReturn(), ...chosenLeave(), ask: confirms.ask });
}

/**
 * The queue window's Leave queue (P1.4-T164, `queue/leave-queue.ts`): tells the gateway, which ends the place it
 * holds, and returns to character select with no question; a `?token=` page, which has no select, waits for the
 * gateway's close.
 *
 * @returns True once the gateway was told, false when the page stayed where it was.
 */
function leaveQueue(): Promise<boolean> {
  return runLeaveQueue({ ...selectReturn(), leave: () => scene?.leaveQueue() ?? false });
}

/**
 * Where this page sends feedback (ADR-081's amendment of 2026-10-05, `feedback/route.ts`): the dev server's request
 * endpoint on a development page, the gateway's `/requests` with the session token for an account session, or
 * nowhere, in which case the menu holds no Feedback entry. A report carries the page's address, the browser's user
 * agent, the world version, the page's log when ticked and a screenshot of the game when ticked.
 */
const feedbackTo = feedbackRoute({ devEndpoint: config === null ? "" : config.requests, account, gatewayOrigin, local: isLocalPage(window.location.hostname), asked: params.get("feedback") });
const feedbackDestination: FeedbackDestination | null =
  feedbackTo === null
    ? null
    : {
        endpoint: feedbackTo.endpoint,
        client: feedbackTo.client,
        fetcher: feedbackTo.kind === "dev" ? devServerFetch : bearerFetch(() => current?.sessionToken ?? null, (target, init) => fetch(target, init)),
        url: () => window.location.href,
        userAgent: () => window.navigator.userAgent,
        worldVersion: () => gameReport.worldVersion,
        log: () => pageLog.text(),
        screenshot: () => (started === null ? Promise.resolve(null) : captureGameScreenshot(started)),
        now: Date.now,
      };

/**
 * Changes the look of the character the tab plays (ADR-094's amendment of 2026-10-06, B12): the three keys go to the
 * gateway with the session's refresh token. A session that is gone ends as a refused refresh ends it (`keepFresh`):
 * the tab stops refreshing, forgets its grant and leaves the world to the sign-in's notice, and no sentence is shown
 * for it. A session that names no character, as one stored by an earlier release may, cannot ask and reads as no
 * answer.
 *
 * @param look The three keys.
 * @param voice The voice, null for none, sent with the look as one change (ADR-094 note 126); once accepted it is the
 *   one the window opens on next, though the world hears it from the character's next join.
 * @returns Accepted, or why not; it never rejects.
 */
async function changeOwnLook(look: Appearance, voice: string | null): Promise<LookOutcome> {
  const session = current;
  if (session === null || session.characterId === undefined) {
    return { ok: false, why: "unanswered", message: "", field: null };
  }
  const outcome = await changeLook(authBase(url), session.refreshToken, session.characterId, look, voice);
  if (outcome.ok) {
    storedVoice = { voice };
  }
  if (!outcome.ok && outcome.why === "lost") {
    stopFresh?.();
    stopFresh = null;
    storeGrant(null);
    scene?.endSignIn("expired");
  }
  return outcome;
}

/**
 * The Party invitations value the last accepted change stored for the page's character, or null when none was made since
 * the page loaded or the character changed: until then the one the session's character list carried is the stored one.
 */
let storedPartyInvites: OfferedInvitation | null = null;

/**
 * Asks the gateway to store a Party invitations value for the page's character (P2.13-T16). An accepted change is the value
 * Settings opens on next; a refused one leaves what stood, so the next Settings shows the truth; a session that is gone
 * ends as a refused refresh ends it (`keepFresh`).
 *
 * @param value The value to store.
 * @returns Nothing; it never rejects.
 */
async function changeOwnPartyInvites(value: OfferedInvitation): Promise<void> {
  const session = current;
  if (session === null || session.characterId === undefined) {
    return;
  }
  const outcome = await changePartyInvites(authBase(url), session.refreshToken, session.characterId, value);
  if (outcome.ok) {
    storedPartyInvites = value;
    return;
  }
  if (outcome.why === "lost") {
    stopFresh?.();
    stopFresh = null;
    storeGrant(null);
    scene?.endSignIn("expired");
  }
}

/**
 * The page's Party invitations setting for Settings (P2.13-T16): the value the session knows, and the change. Only an
 * account session has one.
 */
const partyInvitesSetting: PartyInvitesSetting | undefined = account
  ? {
      get value(): OfferedInvitation {
        return storedPartyInvites ?? current?.partyInvites ?? "map";
      },
      set: (value) => void changeOwnPartyInvites(value),
    }
  : undefined;

/**
 * The voice a change of look last stored for the page's character, or null when none was made since the page
 * loaded: until then the voice the character joined with is the stored one.
 */
let storedVoice: { readonly voice: string | null } | null = null;

/**
 * What the temporary Change look screen needs from the page (`menu/look-screen.ts`), for an account session while
 * the screen is offered: the character's name as the tab's session holds it, the look the page holds for its own
 * character and its voice, the look sheets' pointer the world boot loaded, the request, and the freeze the page's
 * notices hold, heard as it begins or ends whatever notice shows. A `?token=` page has no character row to change
 * and is given none.
 */
const lookScreen: LookScreenPage | undefined =
  !TEMPORARY_LOOK_SCREEN || !account
    ? undefined
    : {
        looks: PAGE_RULES.looks,
        name: () => {
          const names = hudName(current);
          return `${names.firstName} ${names.familyName}`.trim();
        },
        current: () => appearanceOfLook(PAGE_RULES.looks, scene?.ownLook()),
        voice: () => (storedVoice === null ? pageSound.cues.ownVoice : storedVoice.voice),
        hear: (voice) => pageSound.previewVoice(voice),
        source: () => {
          const manifest = scene?.looks?.pointer ?? null;
          return manifest === null ? null : { looks: PAGE_RULES.looks, manifest, base };
        },
        change: changeOwnLook,
        frozen: () => moderation?.feed.frozen() ?? false,
        watchFrozen: (listener) => moderation?.watchFreeze(listener) ?? (() => undefined),
        touch: window.matchMedia("(pointer: coarse)").matches,
      };

menu = mountGameMenu(document.getElementById("menu") as HTMLElement, { look: lookScreen, feedback: feedbackDestination === null || feedbackTo === null ? undefined : { kinds: feedbackTo.kind === "dev" ? "all" : "bug", send: (draft) => sendFeedback(feedbackDestination, draft) }, onInventory: () => bag.setOpen(true), onCharacter: () => character.setOpen(true), emotes: { offer: emoteWheel.menu.offer, open: emoteWheel.menu.open, watch: watchEmotesOffer }, input, zoom, render, frameLimit: benchPage ? undefined : frames, nameplates, healthNumbers, idleMarks, emotesOthers, coordinates: new CoordinatesSetting(minimap), compassPlaces: new CompassPlacesSetting(minimap), autoLoot: pageAutoLoot, barKeys, barPaging, barLock: barLockSetting, barReset, partyInvites: partyInvitesSetting, chatSize, chatText, performance: perf, motion: PAGE_MOTION, audio: pageSound, onView: (before, after) => pageSound.windowMoved(before, after), account, changeCharacter, logOut, logOutEverywhere, leaveLock: account ? { inCombat: () => hud.getSnapshot().vitals?.inCombat ?? false, ask: async () => (await (scene?.askLeave(false) ?? Promise.resolve(null)))?.lock ?? null } : undefined, onClosedWhileConcealed: () => shell.focus() });
input.onMenu(() => {
  gameReport.menuRequests += 1;
  menu?.request();
});

/**
 * The world version a `welcome` named before this page's last reload, taken once from the tab's store (ADR-050's
 * amendment of 2026-10-02); null when there is none or the store is unavailable.
 *
 * @returns The version, or null.
 */
function keptVersionHint(): string | null {
  try {
    return takeVersionHint(window.sessionStorage);
  } catch {
    return null;
  }
}

/**
 * The world is shown and the loading shell has left the page: the HUD and the death screen it covered take input and
 * are read by assistive technology, and the game menu opens on a request from now on.
 */
function revealWorld(): void {
  touch.worldShown(true);
  hudHost.inert = false;
  pageSound.scene(MusicScene.World);
  pageSound.maintenanceEnd();
  readingHost.inert = false;
  dialogueHost.inert = false;
  deathHost.inert = false;
  chat.reveal();
  menu?.reveal();
  moderation?.reveal();
}

/**
 * The page leaves the world it showed (P1.9-T15), before the loading shell comes back over it with the
 * notice: focus leaves whatever had it, so the chat input gives up the keyboard and the notice's action takes
 * focus as it appears with nothing to hand it back to, then the HUD and the chat go inert and the game menu
 * closes, since it stacks over the shell, until the next reveal.
 */
function concealWorld(): void {
  touch.worldShown(false);
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }
  hudHost.inert = true;
  readingHost.inert = true;
  dialogueHost.inert = true;
  deathHost.inert = true;
  chat.hide();
  menu?.conceal();
  moderation?.conceal();
}

const bench = benchPage ? (await import("./bench/mount.ts")).benchFromUrl(params, worldId, "main", config === null ? "" : config.benchReports, () => render.divisor) : null;
if (bench !== null) {
  render.setOverride(parseRenderOverride(params.get("render")));
}
const ownLook = trialLookAllowed(params.get("look"), window.location.hostname) ? await trialLookLoader(params) : null;
const looks = pageFigures(PAGE_RULES.looks, crowdLooksAllowed(params.get("looks"), developmentPage));
scene = new GameScene({
  stick,
  lowDetail: () => render.divisor > 1,
  weatherForce: developmentPage ? parseForcedWeatherByMap(params.get("weather")) : null,
  wetForce: developmentPage ? parseForcedWetByMap(params.get("wet"), params.get("dry")) : null,
  feetMode: developmentPage ? parseFeetMode(params.get("puddlefx")) : "auto",
  drenchedForce,
  ownDrench: (nowMs) => dripLevel(bar.leftMs(drenchedNumber, nowMs) / TICK_MS, PAGE_RULES.effects.get("drenched")?.durationTicks ?? 0),
  effects: () => effectsDetail(PAGE_EFFECTS.choice, render.divisor),
  windForce: developmentPage ? parseForcedWind(params.get("wind")) : null,
  cloudsForce: developmentPage ? parseForcedClouds(params.get("clouds")) : null,
  playerMenu: (who, x, y) => partyStore.openMenu({ ...who, x, y }),
  ...(bench === null ? {} : { director: bench.runner }),
  ...(ownLook === null ? {} : { ownLook }),
  looks,
  ownAppearance: () => current?.look ?? null,
  url,
  token: () => presented,
  latencyMs,
  ...(developmentPage && Number(params.get("handoffDelay") ?? "0") > 0 ? { handoffDelayMs: Number(params.get("handoffDelay")) } : {}),
  base,
  worldId,
  zoom,
  uiScale,
  pointer: worldPointer(params.get("pointer"), config === null ? "" : config.pointer),
  running: () => runningWorldVersion(gatewayOrigin, versionHint),
  input,
  progress: (event) => {
    if (event.type === "left" || event.type === "departed") {
      concealWorld();
    }
    shell.dispatch(event);
    if (event.type === "revealed") {
      revealWorld();
    }
  },
  account,
  ...(admitted.session === null ? {} : { onAuthFailed: endRefusedSession, freshToken }),
  hud,
  chat: partyingFeed(alertingFeed(chatStore, chatAlerts), partyStore),
  emotes: emoteWheel.store,
  minimap,
  worldMap,
  party: partyStore,
  partyPositions,
  emoji: emojiFeed(gameReport.emoji, icons.log),
  reading,
  dialogue,
  maintenance: warnings,
  feedback: {
    offer: () => menu?.offerFeedback(),
    access: (state, endsInMs) => {
      if (feedbackTo?.kind === "gateway") {
        menu?.feedbackAccess(state === "paused" ? { state, until: Date.now() + endsInMs } : { state });
      }
    },
  },
  moderation: moderation.feed,
  badge: (offer) => badgeCommands?.offered(offer),
  run: pageRun,
  bar,
  arranged,
  belt,
  companion,
  bag,
  wallet,
  character,
  travel,
  harvest,
  chest,
  loot: lootToasts,
  levelUp,
  discoveries,
  sounds: pageSound.cues,
  onMaintenanceStart: (answered) => void pageSound.maintenanceStart(answered),
  stance,
  death,
});
const game = new Phaser.Game({ type: Phaser.WEBGL, banner: false, render: GPU_RENDER_CONFIG, parent: host, backgroundColor: "#101418", pixelArt: true, scale: { mode: Phaser.Scale.NONE, width: view.width, height: view.height, zoom: 1 }, scene: [scene] });
started = game;
watchContextLoss(game.renderer, contextLossNotice(document, document.body, "game"));
frames.install(game);
game.events.on(Phaser.Core.Events.STEP, (time: number, delta: number) => {
  barTicker.write(ownClock.read(time));
  perf.frame(delta);
});
releaseFieldOnWorldPress({ host, world: () => game.canvas ?? null, isTextField, focused: () => document.activeElement });
if (developmentPage) {
  (window as unknown as { game: typeof gameReport; phaserGame: Phaser.Game }).game = gameReport;
  (window as unknown as { phaserGame: Phaser.Game }).phaserGame = game;
}
bench?.mount(game, document.body);
const coverScene = scene;
if (isLocalPage(window.location.hostname)) {
  (window as unknown as { gameCovering: (x: number, y: number) => CoveringPiece[] }).gameCovering = (x, y) => coverScene.coveringAt(x, y);
  (window as unknown as { gameFading: (x: number, y: number) => PieceAlpha[] }).gameFading = (x, y) => coverScene.fadingAt(x, y);
}
